import { defineStore } from 'pinia'
import { toTimeMilliseconds } from '@/components/formatters.js';
import Api from '../api/Api'

function fixTimeFormats(subtitle) {
  subtitle.start /= 1000;
  subtitle.end /= 1000;
  let duration = subtitle.end - subtitle.start;
  subtitle.duration = Number(duration.toFixed(1));

  if (subtitle.content.length > 0) {
    subtitle.charsPerSec = (subtitle.content.length / duration).toFixed(1);
  } else {
      subtitle.charsPerSec = '-';
  }

  return subtitle;
}

function sortSubtitles(a, b) {
  if (a.start < b.start) return -1;
  if (a.start > b.start) return 1;
  return 0;
}

export const usePodTranscriptStore = defineStore('podTranscript', {
  state: () => ({
    transcripts: [],
    dynamicVTTFiles: [],
  }),
  getters: {
    getTranscripts: function(state) {
      return state.transcripts;
    },
    getDynamicVTTFiles: function(state) {
      return state.dynamicVTTFiles;
    }
  },
  actions: {
    _setTranscripts: function(transcripts) {
      for (let i = 0; i < transcripts.length; i++) {
        if (!Array.isArray(transcripts[i].sentences)) {
          transcripts[i].sentences = [];
        }

        transcripts[i].sentences.map(fixTimeFormats);
        transcripts[i].sentences.sort(sortSubtitles);
      }

      this.transcripts = transcripts;
    },
    _createTranscript: function(transcript) {
      if (!Array.isArray(!transcript.sentences)) {
        transcript.sentences = [];
      }

      this.transcripts.push(transcript);
    },
    _updateTranscript: function(transcript) {
      if (!Array.isArray(transcript.sentences)) {
        transcript.sentences = [];
      }

      for (let p = 0; p < transcript.sentences.length; p++) {
        Object.assign(transcript.sentences[p], fixTimeFormats(transcript.sentences[p]));
      }

      for (let i = 0; i < this.transcripts.length; i++) {
        if (this.transcripts[i].id === transcript.id) {
          Object.assign(this.transcripts[i], transcript);
          break;
        }
      }
    },
    _deleteTranscript: function(id) {
      for (let i = 0; i < this.transcripts.length; i++) {
        if (this.transcripts[i].id == id) {
          this.transcripts.splice(i, 1);
        }
      }
    },
    _createTranscriptLine: function(sentence) {
      for (let i = 0; i < this.transcripts.length; i++) {
        if (this.transcripts[i].id == sentence.transcriptid) {
          this.transcripts[i].sentences.push(fixTimeFormats(sentence));
          this.transcripts[i].sentences.sort(sortSubtitles);
          break;
        }
      }
    },
    _updateTranscriptLine: function(sentence) {
      for (let p = 0; p < this.transcripts.length; p++) {
        if (this.transcripts[p].id !== sentence.transcriptid) {
          continue;
        }

        for (let i = 0; i < this.transcripts[p].sentences.length; i++) {
          if (this.transcripts[p].sentences[i].id == sentence.id) {
            Object.assign(this.transcripts[p].sentences[i], fixTimeFormats(sentence));
            delete this.transcripts[p].sentences[i]['mldata'];
            break;
          }
        }
        this.transcripts[p].sentences.sort(sortSubtitles);
        break;
      }
    },
    _deleteTranscriptLine: function(sentenceID) {
      for (let p = 0; p < this.transcripts.length; p++) {
        for (let i = 0; i < this.transcripts[p].sentences.length; i++) {
          if (this.transcripts[p].sentences[i].id == sentenceID) {
            this.transcripts[p].sentences.splice(i, 1);
            break;
          }
        }
      }
    },
    _regenerateDynamicVTTFiles: function() {
      for (let i = 0; i < this.dynamicVTTFiles.length; i++) {
        URL.revokeObjectURL(this.dynamicVTTFiles[i].url);
      }
      this.dynamicVTTFiles = [];

      for (let i = 0; i < this.transcripts.length; i++) {
        let str =  "WEBVTT\n\n";
        let sentences = this.transcripts[i].sentences || [];
        for (let p = 0; p < sentences.length; p++) {
          str += toTimeMilliseconds(sentences[p].start) + "0";
          str += " --> ";
          str += toTimeMilliseconds(sentences[p].end) + "0\n";
          str += sentences[p].content + "\n\n";
        }

        let blob = new Blob([str], {type: 'text/vtt'});
        let vttFileStructure = {
          label: this.transcripts[i].name,
          language: "en",
          url: URL.createObjectURL(blob),
          isTranscript: true,
          transcriptId: this.transcripts[i].id,
        };
        this.dynamicVTTFiles.push(vttFileStructure);
      }
    },


    refreshTranscriptLines: async function(podID) {
      const result = await Api.transcript.getTranscriptLines(podID);
      const transcripts = result.data.transcripts || [];
      this._setTranscripts(transcripts);
      this._regenerateDynamicVTTFiles();
      return transcripts;
    },
    generateFile: async function(data) {
      const result = await Api.transcript.generateFile(data.podID, data.id, data.payload);
      console.log("GENERATE VTT result", result);
      return result;
    },
    createTranscript: async function(data) {
      const result = await Api.transcript.createTranscript(data.podID, data.payload);
      this._createTranscript(result.data);
      this._regenerateDynamicVTTFiles();
      return result.data;
    },
    updateTranscript: async function(data) {
      const result = await Api.transcript.updateTranscript(data.podID, data.id, data.payload);
      this._updateTranscript(result.data);
      this._regenerateDynamicVTTFiles();
      return result.data;
    },
    deleteTranscript: async function(data) {
      const result = await Api.transcript.deleteTranscript(data.podID, data.id);
      this._deleteTranscript(data.id);
      this._regenerateDynamicVTTFiles();
      return result.data;
    },
    createTranscriptLine: async function(data) {
      const result = await Api.transcript.createTranscriptLine(data.podID, data.transcriptID, data.payload);
      this._createTranscriptLine(result.data);
      this._regenerateDynamicVTTFiles();
      return result.data;
    },
    updateTranscriptLine: async function(data) {
      const result = await Api.transcript.updateTranscriptLine(data.podID, data.transcriptID, data.payload);
      this._updateTranscriptLine(result.data);
      this._regenerateDynamicVTTFiles();
      return result.data;
    },
    deleteTranscriptLine: async function(data) {
      await Api.transcript.deleteTranscriptLine(data.podID, data.transcriptID, data.subtitleID);
      this._deleteTranscriptLine(data.subtitleID);
      this._regenerateDynamicVTTFiles();
    }
  },
});
